import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, Upload, Calendar, Receipt } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEAMS = ['PM1','PM2','PM3','PM4','PM5','PM6','PM7','PM8','PM9','PM10'];

/**
 * Modal que aparece quando o app recebe um arquivo via Share Extension.
 * - Imagem → Enviar para despesas do Controle Helper
 * - PDF → Criar trabalho no calendário
 */
const ShareReceiverModal = ({ isOpen, onClose, sharedFile, sharedFileType }) => {
  const [mode, setMode] = useState(null); // 'expense' | 'calendar'
  const [team, setTeam] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [customerName, setCustomerName] = useState('');
  const [controlId, setControlId] = useState('');
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && sharedFileType) {
      // Auto-detectar modo pelo tipo
      if (sharedFileType.includes('image')) {
        setMode('expense');
      } else if (sharedFileType.includes('pdf')) {
        setMode('calendar');
      } else {
        setMode(null);
      }
    }
  }, [isOpen, sharedFileType]);

  useEffect(() => {
    if (mode === 'expense' && team) {
      loadWeeks(team);
    }
  }, [team, mode]);

  const loadWeeks = async (t) => {
    try {
      const { data } = await axios.get(`${API}/share/helper-weeks/${t}`);
      setWeeks(data || []);
      if (data.length > 0) setControlId(data[0].id);
    } catch (e) {
      setWeeks([]);
    }
  };

  const submitExpense = async () => {
    if (!team || !controlId || !sharedFile) {
      toast.error('Selecione equipe e semana');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', sharedFile);
      formData.append('team', team);
      formData.append('control_id', controlId);

      await axios.post(`${API}/share/expense`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Despesa enviada com sucesso!');
      onClose();
    } catch (e) {
      toast.error('Erro ao enviar despesa');
    } finally {
      setLoading(false);
    }
  };

  const submitCalendarJob = async () => {
    if (!team || !date || !sharedFile) {
      toast.error('Selecione equipe e data');
      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', sharedFile);
      formData.append('team', team);
      formData.append('date', date);
      formData.append('customer_name', customerName);

      await axios.post(`${API}/share/calendar-job`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Trabalho criado no calendário!');
      onClose();
    } catch (e) {
      toast.error('Erro ao criar trabalho');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'expense' ? <Receipt className="h-5 w-5 text-orange-600" /> : <Calendar className="h-5 w-5 text-blue-600" />}
            {mode === 'expense' ? 'Enviar para Despesas' : mode === 'calendar' ? 'Criar Trabalho' : 'Arquivo Recebido'}
          </DialogTitle>
        </DialogHeader>

        {/* Seletor de modo se tipo ambíguo */}
        {!mode && (
          <div className="space-y-3">
            <p className="text-sm text-gray-600">O que deseja fazer com este arquivo?</p>
            <Button className="w-full bg-orange-600" onClick={() => setMode('expense')}>
              <Receipt className="h-4 w-4 mr-2" /> Enviar para Despesas
            </Button>
            <Button className="w-full bg-blue-600" onClick={() => setMode('calendar')}>
              <Calendar className="h-4 w-4 mr-2" /> Criar Trabalho no Calendário
            </Button>
          </div>
        )}

        {/* Preview do arquivo */}
        {sharedFile && (
          <div className="bg-gray-50 border rounded p-2 flex items-center gap-2">
            <Upload className="h-4 w-4 text-gray-400" />
            <span className="text-sm truncate">{sharedFile.name}</span>
            <Badge variant="outline" className="text-xs ml-auto">{(sharedFile.size / 1024).toFixed(0)} KB</Badge>
          </div>
        )}

        {/* Modo Despesa */}
        {mode === 'expense' && (
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium block mb-1">Equipe</label>
              <select className="w-full border rounded px-3 py-2 text-sm" value={team} onChange={e => setTeam(e.target.value)}>
                <option value="">Selecione...</option>
                {TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {weeks.length > 0 && (
              <div>
                <label className="text-sm font-medium block mb-1">Semana</label>
                <select className="w-full border rounded px-3 py-2 text-sm" value={controlId} onChange={e => setControlId(e.target.value)}>
                  {weeks.map(w => (
                    <option key={w.id} value={w.id}>
                      Sem {w.semana}/{w.ano} ({w.data_inicio} - {w.data_fim})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Modo Calendário */}
        {mode === 'calendar' && (
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium block mb-1">Equipe</label>
              <select className="w-full border rounded px-3 py-2 text-sm" value={team} onChange={e => setTeam(e.target.value)}>
                <option value="">Selecione...</option>
                {TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Data</label>
              <input type="date" className="w-full border rounded px-3 py-2 text-sm" value={date} onChange={e => setDate(e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium block mb-1">Nome do Cliente</label>
              <input className="w-full border rounded px-3 py-2 text-sm" value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Ex: John Smith" />
            </div>
          </div>
        )}

        {mode && (
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMode(null); setTeam(''); }}>Voltar</Button>
            <Button
              onClick={mode === 'expense' ? submitExpense : submitCalendarJob}
              disabled={loading || !team}
              className={mode === 'expense' ? 'bg-orange-600 hover:bg-orange-700' : 'bg-blue-600 hover:bg-blue-700'}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {mode === 'expense' ? 'Enviar Despesa' : 'Criar Trabalho'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ShareReceiverModal;
